import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Download, Printer } from 'lucide-react';
import { WorkOrder } from '../../lib/types';
import { useAppContext } from '../../lib/store';
import { toast } from 'sonner';
interface PdfPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrder: WorkOrder | null;
}
export function PdfPreviewModal({
  open,
  onOpenChange,
  workOrder
}: PdfPreviewModalProps) {
  const {
    customers
  } = useAppContext();
  if (!workOrder) return null;
  const customer = customers.find(c => c.id === workOrder.customer_id);
  const handleDownload = () => {
    toast.success('PDF dokument se prenaša...');
    setTimeout(() => onOpenChange(false), 1000);
  };
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-90">
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0" data-id="element-91">
        <DialogHeader className="p-4 border-b flex flex-row items-center justify-between sticky top-0 bg-white z-10" data-id="element-92">
          <DialogTitle data-id="element-93">Predogled PDF dokumenta</DialogTitle>
          <div className="flex gap-2" data-id="element-94">
            <Button variant="outline" size="sm" onClick={() => toast.success('Pošiljam v tiskalnik...')} data-id="element-95">
              <Printer className="w-4 h-4 mr-2" data-id="element-96" />
              Natisni
            </Button>
            <Button size="sm" onClick={handleDownload} data-id="element-97">
              <Download className="w-4 h-4 mr-2" data-id="element-98" />
              Prenesi PDF
            </Button>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 bg-slate-100 p-8" data-id="element-99">
          <div className="bg-white max-w-3xl mx-auto shadow-sm border min-h-[1056px] p-12 text-sm" data-id="element-100">
            {/* Header */}
            <div className="flex justify-between items-start mb-12" data-id="element-101">
              <div data-id="element-102">
                <img src="https://cdn.magicpatterns.com/uploads/fwae64AkjzR1PZNPwPqUJx/simtech_(1).svg" alt="Simtech" className="h-12 mb-2" data-id="element-103" />
                <p className="text-slate-500 mt-1" data-id="element-104">Simtech d.o.o.</p>
                <p className="text-slate-500" data-id="element-105">1000 Ljubljana</p>
                <p className="text-slate-500 mt-2" data-id="element-106">T: 01 234 5678</p>
                <p className="text-slate-500" data-id="element-107">E: info@simtech.si</p>
              </div>
              <div className="text-right" data-id="element-108">
                <h1 className="text-3xl font-bold text-slate-800 mb-2" data-id="element-109">
                  DELOVNI NALOG
                </h1>
                <p className="text-xl text-slate-600 font-mono" data-id="element-110">
                  {workOrder.work_order_number}
                </p>
                <p className="text-slate-500 mt-4" data-id="element-111">
                  Datum:{' '}
                  {new Date(workOrder.execution_date).toLocaleDateString('sl-SI')}
                </p>
              </div>
            </div>

            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-112">
              <div className="p-4 bg-slate-50 rounded-lg border" data-id="element-113">
                <h3 className="font-semibold text-slate-700 mb-2" data-id="element-114">
                  Naročnik / Stranka:
                </h3>
                <p className="font-bold" data-id="element-115">{customer?.name}</p>
                <p data-id="element-116">{customer?.address}</p>
                <p data-id="element-117">
                  {customer?.postal_code} {customer?.city}
                </p>
                {customer?.tax_number && <p className="mt-2" data-id="element-118">ID za DDV: {customer.tax_number}</p>}
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border" data-id="element-119">
                <h3 className="font-semibold text-slate-700 mb-2" data-id="element-120">
                  Podatki o lokaciji:
                </h3>
                <p data-id="element-121">
                  <span className="text-slate-500" data-id="element-122">Lokacija:</span>{' '}
                  {workOrder.location}
                </p>
                <p data-id="element-123">
                  <span className="text-slate-500" data-id="element-124">Naročnik:</span>{' '}
                  {workOrder.customer_reference_or_orderer}
                </p>
                <p data-id="element-125">
                  <span className="text-slate-500" data-id="element-126">Nujnost:</span>{' '}
                  {workOrder.urgency}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8" data-id="element-127">
              <h3 className="font-semibold text-slate-800 border-b pb-2 mb-4" data-id="element-128">
                Opis opravila
              </h3>
              <p className="whitespace-pre-wrap" data-id="element-129">
                {workOrder.work_description || '/'}
              </p>
            </div>

            {/* Labor */}
            <div className="mb-8" data-id="element-130">
              <h3 className="font-semibold text-slate-800 border-b pb-2 mb-4" data-id="element-131">
                Opravljeno delo
              </h3>
              {workOrder.labor_items.length > 0 ? <table className="w-full text-left border-collapse" data-id="element-132">
                  <thead data-id="element-133">
                    <tr className="border-b bg-slate-50" data-id="element-134">
                      <th className="py-2 px-4 font-semibold" data-id="element-135">Opis</th>
                      <th className="py-2 px-4 font-semibold w-24 text-right" data-id="element-136">
                        Količina
                      </th>
                      <th className="py-2 px-4 font-semibold w-24" data-id="element-137">EM</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-138">
                    {workOrder.labor_items.map((item, i) => <tr key={item.id} className="border-b" data-id="element-139">
                        <td className="py-2 px-4" data-id="element-140">{item.description}</td>
                        <td className="py-2 px-4 text-right" data-id="element-141">
                          {item.quantity}
                        </td>
                        <td className="py-2 px-4" data-id="element-142">{item.unit}</td>
                      </tr>)}
                  </tbody>
                </table> : <p className="text-slate-500 italic" data-id="element-143">Ni vnesenega dela.</p>}
            </div>

            {/* Material */}
            <div className="mb-8" data-id="element-144">
              <h3 className="font-semibold text-slate-800 border-b pb-2 mb-4" data-id="element-145">
                Porabljen material
              </h3>
              {workOrder.material_items.length > 0 ? <table className="w-full text-left border-collapse" data-id="element-146">
                  <thead data-id="element-147">
                    <tr className="border-b bg-slate-50" data-id="element-148">
                      <th className="py-2 px-4 font-semibold" data-id="element-149">
                        Naziv materiala
                      </th>
                      <th className="py-2 px-4 font-semibold w-24 text-right" data-id="element-150">
                        Količina
                      </th>
                      <th className="py-2 px-4 font-semibold w-24" data-id="element-151">EM</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-152">
                    {workOrder.material_items.map((item, i) => <tr key={item.id} className="border-b" data-id="element-153">
                        <td className="py-2 px-4" data-id="element-154">{item.material_name}</td>
                        <td className="py-2 px-4 text-right" data-id="element-155">
                          {item.quantity}
                        </td>
                        <td className="py-2 px-4" data-id="element-156">{item.unit}</td>
                      </tr>)}
                  </tbody>
                </table> : <p className="text-slate-500 italic" data-id="element-157">Ni vnesenega materiala.</p>}
            </div>

            {/* Notes */}
            {workOrder.notes && <div className="mb-12" data-id="element-158">
                <h3 className="font-semibold text-slate-800 border-b pb-2 mb-4" data-id="element-159">
                  Opombe
                </h3>
                <p className="whitespace-pre-wrap" data-id="element-160">{workOrder.notes}</p>
              </div>}

            {/* Signatures */}
            <div className="mt-24 grid grid-cols-2 gap-16" data-id="element-161">
              <div data-id="element-162">
                <Separator className="mb-2" data-id="element-163" />
                <p className="font-semibold" data-id="element-164">Izvajalec</p>
                <p className="text-slate-500" data-id="element-165">
                  {workOrder.technician_name || '_________________'}
                </p>
              </div>
              <div data-id="element-166">
                <Separator className="mb-2" data-id="element-167" />
                <p className="font-semibold" data-id="element-168">Naročnik / Stranka</p>
                <p className="text-slate-500" data-id="element-169">
                  {workOrder.orderer_name || '_________________'}
                </p>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>;
}